import { useEffect, useState } from 'react'
import { RefreshCw, Shuffle, Trash2 } from 'lucide-react'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import type { ControlCohortResponse, ImpactSummary, ProgramResponse } from '@/shared/api/types'
import {
  clearControlCohort, drawControlCohort, getControlCohort, getImpact, refreshImpact,
} from '../api'
import { Excluded, MeasuredWith, PlannedChip, Value } from '../parts'
import { VerdictBadge } from '../vocab'

/**
 * Impact — the tab this whole phase exists for.
 *
 * PollSphere computed `conversionAfter = conversionBefore + expectedUplift`,
 * where `expectedUplift` is a number somebody typed into the programme form at
 * planning time. Every impact figure, every ROI, every quadrant position and
 * every mobilizer league placing in that product is a restatement of one
 * planning field. It cannot be wrong, because it never measured anything.
 *
 * Here:
 *   * uplift is MEASURED or it is NULL — never derived, never zero-filled;
 *   * the planned figure appears, labelled Planned, beside the measured one and
 *     is never an operand of it;
 *   * every number carries how many people it was measured over;
 *   * a causal claim needs a control cohort, and without one the verdict says so
 *     in a sentence rather than leaving a blank cell.
 */
export function ImpactPanel({ program }: { program: ProgramResponse }) {
  const canUpdate = useCan('program:update')
  const [impact, setImpact] = useState<ImpactSummary | null>(null)
  const [cohort, setCohort] = useState<ControlCohortResponse | null>(null)
  const [cohortError, setCohortError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let alive = true
    getImpact(program.id).then((d) => { if (alive) setImpact(d) })
      .catch((e) => { if (alive) setError(explain(e)) })
    // A discarded error here becomes an ASSERTION further down — "No comparison
    // group has been drawn" is a statement about the programme's methodology,
    // and a 403 or a 500 is not that statement.
    getControlCohort(program.id)
      .then((d) => { if (alive) { setCohort(d); setCohortError(null) } })
      .catch((e) => { if (alive) setCohortError(explain(e)) })
    return () => { alive = false }
  }, [program.id])

  const refresh = async () => {
    setBusy(true); setError(null)
    try { const r = await refreshImpact(program.id); setImpact(r.summary) }
    catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  const draw = async () => {
    setBusy(true); setError(null)
    try { setCohort(await drawControlCohort(program.id)) }
    catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  const clear = async () => {
    if (!window.confirm('Discard this control cohort? Redrawing produces a different comparison group.')) return
    setBusy(true)
    try { setCohort(await clearControlCohort(program.id)) }
    catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  if (error && !impact) return <ErrorStrip message={error} />
  if (!impact) return <LoadingBlock label="Loading impact…" />

  const hasControl = (cohort?.size ?? 0) > 0

  return (
    <div className="space-y-4 px-1">
      <ErrorStrip message={error} />

      <div className="flex flex-wrap items-center gap-2">
        <VerdictBadge code={impact.verdictCode} />
        <PlannedChip value={impact.expectedUpliftPp} />
        {impact.measuredAt && (
          <span className="text-erp-xs text-muted-foreground">
            measured {fmtDateTime(impact.measuredAt)}
          </span>
        )}
        {canUpdate && (
          <button type="button" className="erp-btn h-7 text-erp-xs ml-auto"
            onClick={refresh} disabled={busy}>
            <RefreshCw className="h-3.5 w-3.5" /> Re-measure
          </button>
        )}
      </div>

      {/* The explanation is NOT NULL whenever a measurement number is null. A
          blank cell teaches the reader nothing; a sentence tells them what is
          missing and what to do about it. */}
      {impact.explanation && <Note tone="info">{impact.explanation}</Note>}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-md border border-border px-3 py-2">
          <div className="text-erp-xs text-muted-foreground">Measured uplift — beneficiaries</div>
          <div className="text-erp-xl font-semibold">
            <Value value={impact.meanUpliftTreated} suffix=" pp"
              format={(n) => `${n > 0 ? '+' : ''}${n}`}
              reason="Not enough measured readings to report a mean." shortReason />
          </div>
          <MeasuredWith measured={impact.measured} measurable={impact.measurable} />
        </div>
        <div className="rounded-md border border-border px-3 py-2">
          <div className="text-erp-xs text-muted-foreground">Comparison group</div>
          <div className="text-erp-xl font-semibold">
            <Value value={impact.meanUpliftControl} suffix=" pp"
              format={(n) => `${n > 0 ? '+' : ''}${n}`}
              reason="No comparison group of sufficient size has been measured." shortReason />
          </div>
          <MeasuredWith measured={impact.controlMeasured} measurable={impact.controlSize} />
        </div>
        <div className="rounded-md border border-border px-3 py-2 bg-muted/20">
          <div className="text-erp-xs text-muted-foreground">Difference-in-differences</div>
          <div className="text-erp-xl font-semibold">
            <Value value={impact.differenceInDifferences} suffix=" pp"
              format={(n) => `${n > 0 ? '+' : ''}${n}`}
              reason="Needs a measured result on both sides." shortReason />
          </div>
          <p className="text-erp-xs text-muted-foreground">
            what the programme moved beyond what moved anyway
          </p>
        </div>
      </div>

      <Excluded items={[
        { count: impact.excludedUnlinked, label: 'not linked to the register',
          hint: 'No register row, so no support reading to compare.' },
        { count: impact.excludedNoBaseline, label: 'with no support baseline',
          hint: 'Nobody had assessed them before enrolment, so there is nothing to measure from.' },
        { count: impact.excludedNotEnrolled, label: 'shortlisted but never enrolled',
          hint: 'They received nothing, so there is no treatment to measure.' },
      ]} />
      <p className="text-erp-xs text-muted-foreground">
        {fmtNumber(impact.totalBeneficiaries)} beneficiaries in total;{' '}
        {fmtNumber(impact.measurable)} could be measured and {fmtNumber(impact.measured)} were.
      </p>

      <section className="rounded-md border border-border p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-erp-sm font-medium">Comparison group</h4>
          {canUpdate && (
            <div className="flex gap-2">
              <button type="button" className="erp-btn h-7 text-erp-xs" onClick={draw} disabled={busy}>
                <Shuffle className="h-3.5 w-3.5" /> {hasControl ? 'Redraw' : 'Draw'}
              </button>
              {hasControl && (
                <button type="button" className="erp-btn-ghost h-7 text-erp-xs" onClick={clear} disabled={busy}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {cohortError ? (
          <p className="text-erp-xs text-amber-700 dark:text-amber-300">
            The comparison group could not be loaded — {cohortError} Whether one exists is
            unknown, so nothing below should be read as "there is no control".
          </p>
        ) : !hasControl ? (
          <p className="text-erp-xs text-muted-foreground">
            No comparison group has been drawn. Without one, a rise in support during a campaign in
            which everybody's support rose is indistinguishable from an effect of this programme.
          </p>
        ) : (
          <>
            <p className="text-erp-xs text-muted-foreground">
              {fmtNumber(cohort!.size)} matched non-beneficiaries,{' '}
              {fmtNumber(cohort!.measured)} of them re-assessed. Drawn{' '}
              {fmtDateTime(cohort!.drawnAt)}.
            </p>
            {cohort!.stale && (
              <Note tone="bad">
                This cohort is old enough that the people in it may no longer be comparable.
                Redraw before relying on the difference.
              </Note>
            )}
            <Excluded items={[
              { count: cohort!.excludedUnlinked, label: 'beneficiaries unlinked (no stratum)' },
              { count: cohort!.excludedNotEnrolled, label: 'beneficiaries never enrolled' },
              { count: cohort!.excludedIncompleteFields, label: 'beneficiaries missing a matching field' },
              { count: cohort!.skippedThinStrata, label: 'strata too thin to match' },
            ]} />
            {/* The one table in this feature that is not an `ErpTable`; without a
                scroll container it widens the drawer instead of scrolling itself. */}
            {cohort!.strata.length > 0 && (
              <div className="axis-table-scroll">
              <table className="w-full text-erp-xs">
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th className="font-normal py-1">Stratum</th>
                    <th className="font-normal py-1 text-right">Beneficiaries</th>
                    <th className="font-normal py-1 text-right">Controls</th>
                  </tr>
                </thead>
                <tbody>
                  {cohort!.strata.map((s) => (
                    <tr key={s.stratum} className="border-t border-border/50">
                      <td className="py-1 font-mono">{s.stratum}</td>
                      <td className="py-1 text-right tabular-nums">{fmtNumber(s.beneficiaries)}</td>
                      <td className={`py-1 text-right tabular-nums ${
                        s.controls < s.beneficiaries ? 'text-amber-700 dark:text-amber-300' : ''}`}>
                        {fmtNumber(s.controls)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
            {/* Members are non-participants — people offered nothing who
                enrolled in nothing. A "control group" screen that listed them
                would be a targeting list wearing a lab coat. */}
            <p className="text-erp-xs text-muted-foreground italic">
              Counts and strata only. The people in a comparison group were offered nothing and
              consented to nothing, so this screen never lists them.
            </p>
          </>
        )}
      </section>
    </div>
  )
}
